import { Component, OnDestroy, ViewChild, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {HeartBeatService} from '@shared/services/heart-beat.service';
import {SessionService} from '@shared/services/session.service';
import {Permissions} from '@core/constants/permissions';
import {VERSION} from '../assets/version';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {Administration} from "@shared/objects/administration";
import { Router, RouterLinkActive, RouterLink, RouterOutlet } from "@angular/router";
import {OAuthService} from 'angular-oauth2-oidc';
import {authConfig} from '@core/config/auth.config';
import {ConfigUtil} from '@shared/services/config.util';
import {Subscription} from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatToolbar } from '@angular/material/toolbar';
import { MatList, MatListItem, MatDivider } from '@angular/material/list';
import { NgxPermissionsModule } from 'ngx-permissions';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { SelectAdministrationComponent } from './shared/components/select-administration/select-administration.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [MatProgressSpinner, MatSidenavContainer, MatSidenav, MatToolbar, MatList, MatListItem, RouterLinkActive, RouterLink, NgxPermissionsModule, MatDivider, MatIcon, MatIconButton, SelectAdministrationComponent, MatButton, MatMenuTrigger, MatMenu, MatMenuItem, RouterOutlet]
})
export class AppComponent implements OnDestroy {
  readonly breakpointObserver = inject(BreakpointObserver);
  readonly heartBeatService = inject(HeartBeatService);
  private readonly router = inject(Router);
  private readonly oauthService = inject(OAuthService);
  private readonly sessionService = inject(SessionService);

  readonly title:string ;
  firstName = '';
  lastName = '';
  loaded = false;

  private menuOpen = false;
  private largeScreeen = false;
  private subscriptions = new Subscription();
  private timeoutId?: ReturnType<typeof setTimeout>;

  permissions: typeof Permissions = Permissions;
  administration: Administration;
  administrations: Administration[] = [];

  @ViewChild('sidenav')
  private sidenav: MatSidenav;

  constructor() {
    const matIconRegistry = inject(MatIconRegistry);
    const domSanitizer = inject(DomSanitizer);
    matIconRegistry.addSvgIconSet(domSanitizer.bypassSecurityTrustResourceUrl('assets/mdi.svg'));

    const breakpointObserver = this.breakpointObserver;

    this.title = ConfigUtil.get('title');

    this.configure();

    this.subscriptions.add(
      this.sessionService.session$
        .subscribe(value => {
          if (value?.isLoggedIn()) {
            this.firstName = value.user.firstName;
            this.lastName = value.user.lastName;
            this.administration = value.administration;
            this.administrations = value.availableAdministrations;
            this.loaded = true;
          }
        })
    );

    this.subscriptions.add(
      breakpointObserver
        .observe(['(min-width: 800px)'])
        .subscribe((state: BreakpointState) => {
          if (state.matches) {
            this.largeScreeen = true;
          } else {
            this.largeScreeen = false;
          }
        })
    );
  }

  get version(): string {
    return `Uwfactuur versie ${VERSION.version}-${VERSION.pipeline}-${VERSION.commit.substring(0, 6)} `;
  }

  private async configure() {
    this.oauthService.configure(authConfig());
    this.oauthService.timeoutFactor = 0.5; // fires at 50% into lifetime
    this.oauthService.setupAutomaticSilentRefresh();

    await this.oauthService.loadDiscoveryDocumentAndTryLogin();

    if (!this.oauthService.hasValidAccessToken()) {
      this.oauthService.initLoginFlow(); // redirect naar Keycloak login
    }
  }

  public get isMenuOpen() {
    return this.largeScreeen || this.menuOpen;
  }

  public get menuMode() {
    return this.largeScreeen ? 'side' : 'over';
  }

  onMenuItemClick() {
    if (!this.largeScreeen) {
      this.sidenav.close();
    }
  }

  onLogout() {
    this.sessionService.logout();
  }

  myAccount() {
    this.sessionService.myAccount();
  }

  selectAdministration($event: Administration) {
    this.sessionService.setAdministration($event);

    const currentUrl = this.router.url;
    this.router.navigateByUrl('/loading', { skipLocationChange: true }).then(() => {
      this.loaded = false;

      // Clear any existing timeout
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }

      this.timeoutId = setTimeout(() => {
        this.loaded = true;
        this.router.navigate([currentUrl]);
      }, 1000);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
